import { Center, Group, Loader, Stack } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useConfiguratorComponents } from "../../../hooks/useConfiguratorComponents";
import { IconButton } from "../../../mantine/components/IconButton/IconButton";
import { getDefaultMRTOptions } from "../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import type {
  Connection,
  Consumer,
  Meter,
  MeteringOrMarketLocation,
  Person,
  Storage
} from "../../../utils/backend-types";
import { ObjectName } from "../../../utils/enums";
import { downloadAsExcel } from "../../../utils/files/downloadAsExcel";
import { getLinkToComponentEditModal } from "../../../utils/getLinkToComponentEditModal";
import type { GasConnectionResponse } from "../../GasConnectionWizard/GasConnection.types";
import { useShouldShowStaffView } from "../../StaffViewToggle/useShouldShowStaffView";
import { getCustomColumns } from "./columns/getCustomColumns";
import { getIconColumn } from "./columns/getIconColumn";
import { getNameColumn } from "./columns/getNameColumn";
import { ComponentDeleteModal } from "./ComponentDeleteModal/ComponentDeleteModal";
import { useComponentTableExport } from "./hooks/useComponentTableExport";

export type ConfiguratorComponent =
  | Person
  | Consumer
  | Storage
  | Connection
  | GasConnectionResponse
  | Meter
  | MeteringOrMarketLocation;

export const TABLE_IDS: Readonly<Partial<Record<ObjectName, string>>> = {
  [ObjectName.Generator]: "generator-table",
  [ObjectName.Consumer]: "consumer-table",
  [ObjectName.Connection]: "connection-table",
  [ObjectName.GasConnection]: "gas-connection-table",
  [ObjectName.Storage]: "storage-table",
  [ObjectName.Meter]: "meter-table",
  [ObjectName.GhostNode]: "ghost-node-table",
  [ObjectName.MarketLocation]: "market-location-table",
  [ObjectName.MeteringLocation]: "metering-location-table",
  [ObjectName.Weighting]: "weighting-table"
};

interface ComponentTableProps {
  objectName: ObjectName;
  siteId: number;
  variantId: number;
  projectId: string;
  userHasAccess: boolean;
  setShowNotAccessibleModal: (show: boolean) => void;
}

function ComponentTable({
  objectName,
  siteId,
  variantId,
  projectId,
  userHasAccess,
  setShowNotAccessibleModal
}: ComponentTableProps) {
  const { shouldShowStaffView: isStaff } = useShouldShowStaffView();
  const siteOrVariantId = objectName === ObjectName.Person ? variantId : siteId;
  const navigate = useNavigate();
  const { collectDataForExport } = useComponentTableExport();

  const { data: configuratorComponents } = useConfiguratorComponents(
    objectName,
    siteOrVariantId
  );
  const { data: persons } = useConfiguratorComponents<Person>(
    ObjectName.Person,
    variantId
  );
  const { data: meteringOrMarketLocations } =
    useConfiguratorComponents<MeteringOrMarketLocation>(
      ObjectName.MeteringOrMarketLocation,
      siteId
    );

  const [componentToDelete, setComponentToDelete] =
    useState<ConfiguratorComponent | null>(null);
  const [exportIsLoading, setExportIsLoading] = useState(false);

  function isDataLoading() {
    let isExtraDataLoading = false;

    if (
      objectName === ObjectName.Connection ||
      objectName === ObjectName.GasConnection
    ) {
      isExtraDataLoading = !persons;
    } else if (objectName === ObjectName.Meter) {
      isExtraDataLoading = !meteringOrMarketLocations;
    }

    return !configuratorComponents || isExtraDataLoading;
  }

  async function handleClickExport() {
    setExportIsLoading(true);
    const headers = mrtTable
      .getAllColumns()
      .filter((col) => !col.id.includes("mrt-") && col.id !== "icon")
      .map((col) => col.columnDef.header);
    const dataRows = await collectDataForExport({
      mrtTable,
      objectName
    });
    downloadAsExcel([headers, ...dataRows], `${objectName}`);
    setExportIsLoading(false);
  }

  const columns: Array<MRT_ColumnDef<ConfiguratorComponent>> = useMemo(
    () => [
      ...getIconColumn<ConfiguratorComponent>(objectName, siteId),
      getNameColumn({ objectName, projectId, siteId }),
      ...getCustomColumns({
        objectName,
        isStaff,
        persons,
        meteringOrMarketLocations,
        projectId,
        siteId
      })
    ],
    [objectName, siteId, isStaff, persons, meteringOrMarketLocations, projectId]
  );

  const mrtTable = useMantineReactTable<ConfiguratorComponent>({
    ...getDefaultMRTOptions({
      emptyRowsFallbackText: `Keine ${objectName} vorhanden`,
      hideBorder: true
    }),
    ...getDefaultMRTRowEditModeOptions({
      rowActions: {
        edit: {
          text: `${objectName} bearbeiten`,
          onClick: (row) => {
            mrtTable.setEditingRow(null);
            navigate(
              getLinkToComponentEditModal(
                objectName,
                row.original.id,
                projectId,
                siteId
              ) + `/general`
            );
          }
        },
        delete: {
          text: `${objectName} löschen`,
          onClick: (row) => {
            if (!userHasAccess) {
              setShowNotAccessibleModal(true);
            } else {
              setComponentToDelete(row.original);
            }
          }
        }
      }
    }),
    ...getMRTTopToolbarCustomActionsRenderer(),
    enableRowSelection: false,
    renderTopToolbarCustomActions: () => (
      <Group align="center" justify="flex-end" p={0} w="stretch">
        <IconButton
          color="primary"
          icon={IconDownload}
          loading={exportIsLoading}
          size="xs"
          onClick={handleClickExport}
        >
          Als Excel-Datei herunterladen
        </IconButton>
      </Group>
    ),
    layoutMode: "grid",
    enablePagination: false,
    enableColumnResizing: true,
    columns: columns,
    data: configuratorComponents || []
  });

  if (isDataLoading()) {
    return (
      <Center m="lg">
        <Loader />
      </Center>
    );
  }

  return (
    <Stack className="ComponentTable" gap="sm" id={TABLE_IDS[objectName]}>
      <MantineReactTable table={mrtTable} />
      {componentToDelete && (
        <ComponentDeleteModal
          component={componentToDelete}
          objectName={objectName}
          onDeleteSuccess={() => setComponentToDelete(null)}
          onToggle={() => setComponentToDelete(null)}
        />
      )}
    </Stack>
  );
}

export { ComponentTable, ComponentTableProps };
